import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MapPin, Link } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ContactCreatorModal } from '@/components/creator/ContactCreatorModal';
import { getFirstImageUrl, getCurrencySymbol, getGoogleMapsUrl } from '@/lib/utils/bookingHelpers';

export function BookingsSummary({ bookings, user }: { bookings: any[], user: any }) {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [creatorContact, setCreatorContact] = useState({ email: '', instagram: '', phone: '' });

  // Bookings are already pre-filtered and sorted by the parent component
  const userBookings = bookings;
  


  return (
    <div className="bg-earth-800/50 p-6 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-display">Recent Bookings</h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/bookings')}
        >
          View All
        </Button>
      </div>

      {userBookings.length > 0 ? (
        <div className="space-y-4">
          {userBookings.map((booking) => {
            // Use the experience data from the booking (fetched with proper relations)
            const experience = booking.experience;
            if (!experience) return null;

            return (
              <div 
                key={booking.id}
                className="flex items-start gap-4 p-4 bg-earth-800 rounded-lg cursor-pointer group"
                onClick={() => navigate(`/experiences/${experience.id}`)}
              >
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={getFirstImageUrl(experience)}
                    alt={experience.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sand-100 mb-2 truncate">{experience.title}</h3>
                  <p className="text-sm font-semibold text-sand-300 mb-2">
                    {getCurrencySymbol(experience.pricing_config?.currency)}{booking.total_amount}
                  </p>
                  {experience.creator && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={e => {
                        e.stopPropagation();
                        setCreatorContact({
                          email: experience.creator?.creator_profile?.contact_email || 'info@letsrendezvous.co',
                          instagram: experience.creator?.creator_profile?.social_links?.instagram || 'https://www.instagram.com/letsrendez.vous/',
                          phone: (experience.creator && 'phone' in experience.creator && (experience.creator as any).phone) || '',
                        });
                        setShowContactModal(true);
                      }}
                    >
                      Contact {experience.creator.creator_profile?.business_name || experience.creator.full_name || 'Creator'}
                    </Button>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 min-w-[120px]">
                  <div className={`px-2 py-1 rounded-full text-xs mb-2 ${
                    booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!booking.share_link}
                    onClick={e => {
                      e.stopPropagation();
                      if (booking.share_link) {
                        navigator.clipboard.writeText(booking.share_link);
                        setCopiedId(booking.id);
                        setTimeout(() => setCopiedId(null), 1500);
                      }
                    }}
                  >
                    {copiedId === booking.id ? 'Link Copied!' : 'Share'}
                  </Button>
                  {experience.location && (
                    <div className="flex items-center gap-2 text-xs text-sand-400 mt-2">
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{experience.location.city || experience.location.name}</span>
                      {experience.location.address && (
                        <a 
                          href={getGoogleMapsUrl(experience.location)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sand-100 underline ml-auto flex-shrink-0 flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link className="h-3 w-3" />
                          <span className="hidden sm:inline">Map</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sand-400 mb-4">No bookings yet</p>
          <Button onClick={() => navigate('/experiences')}>
            Explore Experiences
          </Button>
        </div>
      )}
      <ContactCreatorModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        email={creatorContact.email}
        instagram={creatorContact.instagram}
        phone={creatorContact.phone}
      />
    </div>
  );
}